import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { categories } from '@/mocks/categories';
import { getComicsByCategory } from '@/mocks/comics';
import type { ComicData } from '@/mocks/comics';

const sortOptions = [
  { value: 'popular', label: '最受欢迎' },
  { value: 'latest', label: '最近更新' },
  { value: 'rating', label: '评分最高' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');
  const [sortBy, setSortBy] = useState('popular');
  const [comics, setComics] = useState<ComicData[]>([]);

  const currentCategory = categories.find((c) => c.slug === activeSlug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    let result = getComicsByCategory(activeSlug);
    if (sortBy === 'latest') {
      result = [...result].sort(
        (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else {
      result = [...result].sort((a, b) => b.views - a.views);
    }
    setComics(result);
  }, [activeSlug, sortBy]);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {/* Breadcrumb */}
          <div className="py-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: currentCategory?.name ? `${currentCategory.name}漫画` : '分类' },
              ]}
            />
          </div>

          {/* Header */}
          <div className="py-6 md:py-10">
            <h1 className="text-2xl md:text-3xl font-heading text-foreground-950">
              {currentCategory?.name}韩国漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2 max-w-xl">
              {currentCategory?.description || '浏览精选韩国漫画作品'}
            </p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveSlug(cat.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5 text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Sort */}
          <div className="flex items-center justify-between mt-6 mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{comics.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
              {sortOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap ${
                    sortBy === opt.value
                      ? 'bg-background-50 text-foreground-900'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          {comics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5 pb-16">
              {comics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </div>
              <h3 className="text-lg font-heading text-foreground-700 mb-2">暂无作品</h3>
              <p className="text-sm text-foreground-500">该分类下暂时没有漫画作品，敬请期待~</p>
              <Link
                to="/"
                className="mt-6 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}